import { CoreInput, CoreSelect } from '@components'
import CalendarIcon from '@images/icons/filter-calendar.svg?react'
import { createBankChangeHandler, getSelectedBankId } from '@views/recipients/utils/bankHelpers'
import {
  BankFieldInputTypes,
  CountrySupportedPurposes,
  getBankMetadataOptions,
  getExternalBankAccountFieldLabel,
  InstrumentFields,
  useCountries
} from 'mmfintech-backend-api'
import type { BankFieldMetadata, BankResponse, SelectOptions } from 'mmfintech-backend-api/types'
import { fixDateOnly, isValidArray, tr } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import { useEffect } from 'react'

const DEFAULT_FIELD_LABELS: Partial<Record<string, string>> = {
  [InstrumentFields.REGISTRATION_NUMBER]: 'Registration number',
  [InstrumentFields.COUNTRY_OF_INCORPORATION]: 'Country of incorporation',
  [InstrumentFields.COUNTRY_OF_CITIZENSHIP]: 'Country of citizenship'
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/

// The form keeps calendar values as ISO yyyy-MM-dd strings, so the metadata regex validates what is submitted.
// The picker needs a local-midnight Date, as new Date('yyyy-MM-dd') is UTC and shifts the day in western timezones.
const isoToPickerDate = (value: unknown): Date | string | undefined => {
  const match = typeof value === 'string' ? ISO_DATE.exec(value) : null
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : (value as string | undefined)
}

type BankMetadataFieldInputProps = {
  field: BankFieldMetadata
  formValues: UseFormValuesOutput
  countryCode: string
  accountTypes: () => SelectOptions
  providedBanks: BankResponse[]
  documentTypesOptions: SelectOptions
}

const BankMetadataFieldInput = (props: BankMetadataFieldInputProps) => {
  const { field, formValues, countryCode, accountTypes, providedBanks, documentTypesOptions } = props

  const { countryOptions } = useCountries(CountrySupportedPurposes.WITHDRAWAL)

  const providedBanksOptions = () =>
    isValidArray(providedBanks)
      ? providedBanks
          .filter((v: BankResponse) => !!v.code?.length)
          .map((bank: BankResponse) => ({ value: bank.id, label: bank.name }))
      : []

  const findBankByName = (bankName: string) => {
    const found = providedBanks?.find((bank: BankResponse) => bank.name === bankName)
    if (found) {
      formValues.setValue('bankCode', found.code)
      formValues.setValue('bankName', found.name)
    }
  }

  const handleBankChange = createBankChangeHandler(providedBanks, formValues)

  useEffect(() => {
    if (!formValues.getValue('bankCode') && providedBanks) {
      findBankByName(formValues.getValue('bankName'))
    }
  }, [formValues.getValue('bankName'), formValues.getValue('bankCode'), providedBanks])

  const showBankDropDown = countryCode !== 'BRA' && isValidArray(providedBanks)

  if (!field) {
    return null
  }

  const fieldLabel = getExternalBankAccountFieldLabel(countryCode, field.code, DEFAULT_FIELD_LABELS[field.code])

  if (field.code === InstrumentFields.BANK_CODE) {
    return showBankDropDown ? null : (
      <CoreInput
        type='text'
        data-test={field.code}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
      />
    )
  }

  if (field.code === InstrumentFields.BANK_NAME) {
    if (showBankDropDown) {
      const selectedBankId = getSelectedBankId(providedBanks, formValues.getValue('bankName'))

      return (
        <>
          <CoreSelect
            type='default'
            options={providedBanksOptions()}
            label={tr('FRONTEND.RECIPIENTS.LABELS.BANK_NAME', 'Bank Name')}
            value={selectedBankId}
            onChange={handleBankChange}
            required={formValues.isRequired('bankName')}
            error={formValues.getError('bankName')}
          />
          <CoreInput
            type='text'
            readOnly={true}
            value={formValues.getValue('bankCode')}
            label={tr(`FRONTEND.RECIPIENTS.LABELS.BANK_CODE`, 'Bank code')}
            data-test='bank-code'
            {...formValues.registerInput('bankCode')}
          />
        </>
      )
    }

    return (
      <CoreInput
        type='text'
        data-test={field.code}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
      />
    )
  }

  if (field.code === InstrumentFields.DATE_OF_BIRTH || field.fieldType === 'CALENDAR') {
    return (
      <CoreInput
        type='date'
        data-test={field.code}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
        value={isoToPickerDate(formValues.getValue(field.fieldName))}
        onChange={(name: string, date: Date) => formValues.setValue(name, fixDateOnly(date))}
        dateFormat='MM/dd/yyyy'
        LeftIcon={<CalendarIcon />}
      />
    )
  }

  if (
    field.code === InstrumentFields.HOLDER_COUNTRY ||
    field.code === InstrumentFields.COUNTRY_OF_INCORPORATION ||
    field.code === InstrumentFields.COUNTRY_OF_CITIZENSHIP ||
    field?.fieldType === BankFieldInputTypes.COUNTRY
  ) {
    return (
      <CoreSelect
        type='country'
        data-test={field.code}
        options={countryOptions}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
      />
    )
  }

  if (field.code === InstrumentFields.NATIONAL_ID_TYPE) {
    return (
      <CoreSelect
        type='default'
        data-test={field.code}
        options={documentTypesOptions}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
      />
    )
  }

  if (field.code === InstrumentFields.ACCOUNT_TYPE) {
    return (
      <CoreSelect
        type='default'
        data-test={field.code}
        options={accountTypes()}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
      />
    )
  }

  if (field.code === InstrumentFields.PHONE) {
    return (
      <CoreInput
        type='phone'
        data-test={field.code}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
      />
    )
  }

  // should be handled by field.fieldType === 'SELECTION'
  // if (field.code === InstrumentFields.ENTITY_TYPE) {
  //   return (
  //     <CoreSelect
  //       type='default'
  //       data-test={field.code}
  //       isSearchable={false}
  //       preselectedValue={{
  //         value: 'COMPANY',
  //         label: tr('FRONTEND.SIGNUP.ACCOUNT_TYPES.COMPANY', 'Corporate Account')
  //       }}
  //       options={[
  //         {
  //           value: 'INDIVIDUAL',
  //           label: tr('FRONTEND.SIGNUP.ACCOUNT_TYPES.INDIVIDUAL', 'Individual Account')
  //         },
  //         {
  //           value: 'COMPANY',
  //           label: tr('FRONTEND.SIGNUP.ACCOUNT_TYPES.COMPANY', 'Corporate Account')
  //         }
  //       ]}
  //       label={fieldLabel}
  //       tooltip={tr(formValues.getTooltip(field.fieldName), '')}
  //       {...formValues.registerInput(field.fieldName)}
  //     />
  //   )
  // }

  if (field?.fieldType === BankFieldInputTypes.SELECTION) {
    const options = getBankMetadataOptions(field?.code, field?.selectionValues)
    return (
      <CoreSelect
        type='default'
        data-test={field.code}
        options={options}
        label={fieldLabel}
        // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
        {...formValues.registerInput(field.fieldName)}
      />
    )
  }

  return (
    <CoreInput
      type={field.digitsOnly ? 'number' : 'text'}
      data-test={field.code}
      label={fieldLabel}
      // tooltip={tr(formValues.getTooltip(field.fieldName), '')}
      {...formValues.registerInput(field.fieldName)}
      autoComplete='off'
    />
  )
}

export default BankMetadataFieldInput
